import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Organization } from '@/types/organizationTypes';
import { StripeConnectRequest } from '@/services/payment/types';

export const useStripeAccountOperations = (
  organization: Organization | null, 
  organizationId: string | undefined
) => {
  const [loading, setLoading] = useState(false);
  const [requestPending, setRequestPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [platformSetupRequired, setPlatformSetupRequired] = useState(false);
  
  const checkPendingRequest = useCallback(async () => {
    if (!organizationId) return;
    
    try {
      // Use from() and type assertion to avoid TypeScript errors
      const { data, error } = await supabase
        .from('stripe_connect_requests' as any)
        .select('*')
        .eq('organization_id', organizationId)
        .eq('status', 'pending')
        .single();
        
      if (error && error.code !== 'PGRST116') {
        console.error("Error checking pending request:", error);
        return;
      }
      
      if (data) {
        setRequestPending(true);
      } else {
        setRequestPending(false);
      }
    } catch (err) {
      console.error("Error in checkPendingRequest:", err);
    }
  }, [organizationId]);
  
  const createConnectAccount = useCallback(async () => {
    if (!organizationId) {
      toast.error("Missing organization information");
      return;
    }
    
    setLoading(true);
    setErrorMessage(null);
    setPlatformSetupRequired(false);
    
    try {
      // Check for existing pending request
      const { data: existingRequest, error: existingError } = await supabase
        .from('stripe_connect_requests' as any)
        .select('*')
        .eq('organization_id', organizationId)
        .eq('status', 'pending')
        .single();

      if (existingRequest) {
        setRequestPending(true);
        toast("Your Stripe Connect request is under review", {
          description: "Hivetivity Administrators are reviewing your submission. You cannot submit another request until this one is resolved.",
          style: { background: '#e0e7ff', color: '#1e293b' }
        });
        return;
      }

      const billingEmail = organization?.billing_settings?.billing_email || organization?.contact_email || "";
      
      const { data, error } = await supabase.functions.invoke("create-connect-account", {
        body: {
          organizationId,
          email: billingEmail,
          name: organization?.name || "Your Business"
        }
      });
      
      if (error) throw error;
      
      if (data?.error) {
        setErrorMessage(data.error);
        setPlatformSetupRequired(!!data.platformSetupRequired);
        throw new Error(data.error);
      }
      
      if (data?.url) {
        window.location.href = data.url;
      } else if (data?.status === 'pending' || data?.message?.toLowerCase().includes('pending approval')) {
        setRequestPending(true);
        toast.success("🎉 Stripe Connect Request Submitted Successfully!", {
          description: "Your request has been sent to Hivetivity administrators for review. You'll be notified when approved and can then complete the Stripe onboarding process.",
          duration: Infinity,
          action: {
            label: "View Status",
            onClick: () => {
              // This will show the current request status in the UI
              checkPendingRequest();
            }
          }
        });
      } else {
        throw new Error("No URL returned for Stripe Connect onboarding");
      }
    } catch (error) {
      console.error("Stripe Connect Error:", error);
      toast.error("Failed to set up Stripe Connect", {
        description: error instanceof Error ? error.message : "Please try again"
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, organization]);

  const disconnectStripeAccount = useCallback(async () => {
    if (!organizationId || !organization?.billing_settings?.stripe_account_id) return;
    
    if (!confirm("Are you sure you want to disconnect your Stripe account? This will prevent you from receiving payments through the platform.")) {
      return;
    }
    
    setLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("disconnect-stripe-connect", {
        body: { organizationId }
      });
      
      if (error) throw error;
      
      const newBillingSettings = {
        ...organization.billing_settings,
        stripe_account_id: null
      };
      
      const { error: updateError } = await supabase
        .from('organizations')
        .update({ 
          billing_settings: newBillingSettings
        })
        .eq('id', organizationId);
        
      if (updateError) throw updateError;
      
      toast.success("Stripe account disconnected successfully");
    } catch (error) {
      console.error("Error disconnecting Stripe account:", error);
      toast.error("Failed to disconnect Stripe account");
    } finally {
      setLoading(false);
    }
  }, [organizationId, organization]);

  return {
    loading,
    requestPending,
    errorMessage,
    platformSetupRequired,
    createConnectAccount,
    disconnectStripeAccount,
    checkPendingRequest
  };
};
